import {
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    MenuItem,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Spacer,
    useDisclosure,
    useToast,
} from '@chakra-ui/react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
    ActiveNoticeFacets, BookingMethod, Notice, EntryMethodPreferenceOption,
} from '@acp/shared';
import { PropertyAddressCard } from '../../../property/components/property-address-card';
import { useOneOffENMutation } from '../../hooks/use-one-off-en-mutation';
import { OneOffENFormValues, OneOffENSchema } from './form-schema';
import { BookingServiceSelect } from '../booking-modal/booking-service-select';

export function OneOffENModel({
    isOpen,
    onClose,
    propertyId,
    noticeId,
}: {
    isOpen: boolean;
    onClose: () => void;
    propertyId: number;
    noticeId: number;
}) {
    const toast = useToast();

    const form = useForm<OneOffENFormValues>({
        resolver: zodResolver(OneOffENSchema),
        defaultValues: {
            serviceIds: '',
        },
    });

    const _onClose = () => {
        form.reset();

        onClose();
    };

    const oneOffENMutation = useOneOffENMutation(propertyId, {
        onSuccess: () => {
            toast({
                title: 'Request Succeed',
                description:
                    'One-off entry notification will be applied to the next booking for this property', // TODO: change wording
                status: 'success',
                isClosable: true,
            });
            _onClose();
        },
        onError: (error) => {
            toast({
                title: 'Request Failed',
                description: error.message,
                status: 'error',
                isClosable: true,
            });
        },
    });

    const onSubmit = (data: OneOffENFormValues) => {
        oneOffENMutation.mutate({ serviceIds: data.serviceIds, noticeId });
    };

    return (
        <Modal isOpen={isOpen} autoFocus={false} onClose={_onClose} size="2xl">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Request one-off entry notification</ModalHeader>
                <ModalBody>
                    <PropertyAddressCard w="50%" mb={5} propertyId={propertyId} />

                    <Flex alignItems="center" w="50%" mb={6}>
                        <Controller
                            control={form.control}
                            name="serviceIds"
                            render={({ field, fieldState }) => (
                                <FormControl
                                    isInvalid={fieldState.invalid}
                                    isRequired
                                    mb={2}
                                >
                                    <FormLabel htmlFor="none">
                                        Select a service
                                    </FormLabel>
                                    <BookingServiceSelect
                                        propertyId={propertyId}
                                        value={field.value}
                                        onChange={(service) => {
                                            field.onChange(service?.value);
                                        }}
                                        returnServiceIds
                                    />
                                    {fieldState.error && (
                                        <FormErrorMessage>
                                            {fieldState.error.message}
                                        </FormErrorMessage>
                                    )}
                                </FormControl>
                            )}
                        />
                    </Flex>
                </ModalBody>
                <ModalFooter justifyContent="left" pt={10}>
                    <Button
                        onClick={_onClose}
                        colorScheme="muted"
                        variant="ghost"
                        textDecor="underline"
                    >
                        Discard
                    </Button>
                    <Spacer />
                    <Button
                        onClick={form.handleSubmit(onSubmit)}
                        variant="solid"
                        colorScheme="secondary"
                        isLoading={oneOffENMutation.isPending}
                        loadingText="Requesting"
                    >
                        Request
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export function RequestOneOffENButton({ notice }: { notice: Notice }) {
    const _model = useDisclosure();

    let menuButton = <MenuItem onClick={_model.onOpen}>Request one-off EN</MenuItem>;

    let hideButton = false;

    // check if EN is allowed already
    if (notice.property.entryMethodPreferences != null) {
        const allowedOptions = [
            EntryMethodPreferenceOption.YES,
            EntryMethodPreferenceOption.BY_EXCEPTION,
        ];

        let preference = notice.property.entryMethodPreferences;
        if (allowedOptions.indexOf(preference.occupantAppointmentPreference) < 0) {
            hideButton = true;
        }
    } else {
        // TODO: remove this part when we release new entry method
        const allowedRequestTypes = [
            BookingMethod.AppointmentOnly,
            BookingMethod.AppointmentPreference,
        ];

        if (allowedRequestTypes.indexOf(notice.property.bookingMethod) < 0) {
            hideButton = true;
        }
    }

    if (notice.tags.includes(ActiveNoticeFacets.Vacant)) {
        hideButton = true;
    }

    if (notice.tags.includes(ActiveNoticeFacets.UpdateRequired)) {
        hideButton = true;
    }

    if (hideButton) {
        return null;
    }

    return (
        <>
            {menuButton}
            <OneOffENModel
                isOpen={_model.isOpen}
                onClose={_model.onClose}
                propertyId={notice.property.compliancePropertyId}
                noticeId={notice.id}
            />
        </>
    );
}
